import { Controller } from "@hotwired/stimulus"

const REACTION_BUDGET_MS = 100

export default class extends Controller {
  connect() {
    this.primary = document.querySelector("[data-controller~='canvas-activity']") === this.element
    if (!this.primary) return

    this.onSubmit = this.startFromSubmit.bind(this)
    this.onAction = this.startFromAction.bind(this)
    this.onPointerDown = this.rememberControl.bind(this)
    this.onRequest = this.markInitialized.bind(this)
    this.onComplete = this.markComplete.bind(this)
    this.onVisible = this.markVisible.bind(this)
    this.onError = this.finish.bind(this)

    document.addEventListener("submit", this.onSubmit, true)
    this.element.addEventListener("pointerdown", this.onPointerDown, true)
    document.addEventListener("canvas-activity:start", this.onAction)
    document.addEventListener("canvas-activity:init", this.onRequest)
    document.addEventListener("canvas-activity:complete", this.onComplete)
    document.addEventListener("canvas-activity:visible", this.onVisible)
    document.addEventListener("turbo:before-fetch-request", this.onRequest)
    document.addEventListener("turbo:before-render", this.onComplete)
    document.addEventListener("turbo:submit-end", this.onComplete)
    document.addEventListener("turbo:fetch-request-error", this.onError)
    document.addEventListener("turbo:render", this.onVisible)

    if (window.canvasPerformancePending) {
      this.startedAt = window.canvasPerformancePending.startedAt
      this.initializedAt = window.canvasPerformancePending.initializedAt
      this.completedAt = window.canvasPerformancePending.completedAt
      this.currentAction = window.canvasPerformancePending.action
      requestAnimationFrame(() => this.markVisible())
    }
  }

  disconnect() {
    if (!this.primary) return

    document.removeEventListener("submit", this.onSubmit, true)
    this.element.removeEventListener("pointerdown", this.onPointerDown, true)
    document.removeEventListener("canvas-activity:start", this.onAction)
    document.removeEventListener("canvas-activity:init", this.onRequest)
    document.removeEventListener("canvas-activity:complete", this.onComplete)
    document.removeEventListener("canvas-activity:visible", this.onVisible)
    document.removeEventListener("turbo:before-fetch-request", this.onRequest)
    document.removeEventListener("turbo:before-render", this.onComplete)
    document.removeEventListener("turbo:submit-end", this.onComplete)
    document.removeEventListener("turbo:fetch-request-error", this.onError)
    document.removeEventListener("turbo:render", this.onVisible)
  }

  rememberControl(event) {
    this.lastControl = event.target.closest("button, a, [role='button'], input")
  }

  startFromSubmit(event) {
    if (event.target.dataset.captureExclude === "true") return
    this.start(
      event.submitter || this.lastControl || document.activeElement,
      event.target.dataset.canvasPerformanceAction
    )
  }

  startFromAction(event) {
    this.start(
      event.detail?.control || this.lastControl || document.activeElement,
      event.detail?.action
    )
  }

  start(control, action) {
    if (this.startedAt) return

    this.startedAt = performance.now()
    this.initializedAt = null
    this.completedAt = null
    this.currentAction = action || "canvas_action"
    window.canvasPerformancePending = {
      startedAt: this.startedAt,
      initializedAt: null,
      completedAt: null,
      action: this.currentAction
    }
    this.busyControl = control
    this.showSpinner(control)

    requestAnimationFrame(() => {
      this.record({ phase: "reaction", elapsedMs: performance.now() - this.startedAt, budgetMs: REACTION_BUDGET_MS })
    })
  }

  markInitialized() {
    if (!this.startedAt || this.initializedAt) return
    this.initializedAt = performance.now()
    window.canvasPerformancePending.initializedAt = this.initializedAt
    this.record({ phase: "init", elapsedMs: this.initializedAt - this.startedAt })
  }

  markComplete() {
    if (!this.startedAt || this.completedAt) return
    this.completedAt = performance.now()
    window.canvasPerformancePending.completedAt = this.completedAt
    this.record({ phase: "complete", elapsedMs: this.completedAt - this.startedAt })
  }

  markVisible() {
    if (!this.startedAt) return
    this.markComplete()
    this.record({ phase: "visible", elapsedMs: performance.now() - this.startedAt })
    this.finish()
  }

  finish() {
    if (!this.startedAt) return
    this.restoreControl()
    this.startedAt = null
    this.initializedAt = null
    this.completedAt = null
    this.busyControl = null
    this.currentAction = null
    window.canvasPerformancePending = null
  }

  showSpinner(control) {
    if (!control) return
    if (control.matches("input, select, textarea")) control = control.parentElement
    this.busyControl = control

    control.setAttribute("aria-busy", "true")
    control.querySelectorAll(":scope > i, :scope > svg").forEach((icon) => icon.classList.add("hidden"))

    const spinner = document.createElement("i")
    spinner.className = "fa-light fa-spinner-third fa-spin"
    spinner.setAttribute("aria-hidden", "true")
    spinner.dataset.canvasActivitySpinner = "true"
    control.prepend(spinner)
  }

  restoreControl() {
    if (!this.busyControl?.isConnected) return
    this.busyControl.removeAttribute("aria-busy")
    this.busyControl.querySelector("[data-canvas-activity-spinner]")?.remove()
    this.busyControl.querySelectorAll(":scope > i.hidden, :scope > svg.hidden").forEach((icon) => icon.classList.remove("hidden"))
  }

  record(entry) {
    const detail = { action: this.currentAction, ...entry, recordedAt: performance.now() }
    window.canvasPerformance ||= []
    window.canvasPerformance.push(detail)
    this.dispatch("phase", { detail })
  }
}
